import React, { useState, useMemo, useCallback } from 'react';
import { X, ChevronUp, ChevronDown, RotateCcw } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine
} from 'recharts';
import { blackScholes, findBreakevenPrice } from '../utils/blackScholes';
import { BASELINE_MODES } from '../utils/watchlistBaseline';

const RISK_FREE_RATE = 0.05;

// Hot buttons for underlying gain % (matches SimulationSettingsPanel)
const HOT_BUTTONS = [-50, -35, -20, -10, 10, 20, 35, 50, 100, 200, 1000];

/**
 * PnLSimulatorModal — Robinhood-style P&L simulation chart
 * X-axis = time (Now → Expiration), showing theta decay at a given price target.
 * Clicking the chart updates the Days to Exit.
 * Price target slider shows % gain from current underlying + actual dollar price.
 */
export default function PnLSimulatorModal({ isOpen, onClose, rowData, baselineMode }) {
  if (!isOpen || !rowData) return null;
  return <PnLSimulatorContent rowData={rowData} onClose={onClose} baselineMode={baselineMode} />;
}

export function PnLSimulatorContent({ rowData, onClose, baselineMode }) {
  // Extract contract data — normalize field names for both watchlist preset
  // ("Current Underlying", no DTE field) and screener ("Underlying Price", "Days Until Expiration")
  const ticker = rowData['Ticker'];
  const optionType = rowData['Option Type']?.toLowerCase();
  const positionType = (rowData['Position Type'] || 'Buy').toLowerCase();
  const strike = parseFloat(rowData['Strike']);
  const currentPremium = parseFloat(rowData['Current Premium']);
  const underlyingPrice = parseFloat(rowData['Underlying Price'] || rowData['Current Underlying']);

  // Calculate DTE: prefer explicit field, fall back to computing from Expiration date
  let daysUntilExp = parseInt(rowData['Days Until Expiration']);
  if (isNaN(daysUntilExp) && rowData['Expiration']) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expDate = new Date(rowData['Expiration']);
    expDate.setHours(0, 0, 0, 0);
    daysUntilExp = Math.max(1, Math.ceil((expDate - today) / (1000 * 60 * 60 * 24)));
  }

  // Defensive: rowData['Implied Volatility'] can be null / "NA" for legacy or expired contracts.
  // Without this guard, parseFloat -> NaN, which cascades through Black-Scholes and breaks the chart.
  // Falls back to 30% to match the same default used in WatchlistTable.js and the backend engine.
  const ivRaw = parseFloat(rowData['Implied Volatility']);
  const iv = (Number.isFinite(ivRaw) && ivRaw > 0 ? ivRaw : 30) / 100;
  const delta = rowData['Delta'];
  const theta = rowData['Theta'];
  const isSell = positionType === 'sell';

  // Baseline (entry price) for P&L. When the watchlist toggle is "avg_cost",
  // use the user's actual cost basis; otherwise (default for non-watchlist
  // callers) anchor against the current market premium.
  const avgCostRaw = parseFloat(rowData['Average Cost per Contract']);
  const baselinePremium = (baselineMode === BASELINE_MODES.AVG_COST && Number.isFinite(avgCostRaw) && avgCostRaw > 0)
    ? avgCostRaw
    : currentPremium;

  // State: Days to exit
  const [daysToExit, setDaysToExit] = useState(Math.floor(daysUntilExp / 2));

  // State: Underlying gain % (drives priceTarget)
  const [underlyingGainPct, setUnderlyingGainPct] = useState(0);

  // State: Custom IV (null = use original from data)
  const [customIv, setCustomIv] = useState(null);
  const effectiveIv = customIv !== null ? customIv : iv;
  const ivModified = customIv !== null && customIv !== iv;

  // Derived price target from gain %
  const priceTarget = underlyingPrice * (1 + underlyingGainPct / 100);

  // Today's date for X-axis labels
  const today = useMemo(() => new Date(), []);

  // P&L curve over TIME at the current price target
  const pnlData = useMemo(() => {
    const step = Math.max(1, Math.floor(daysUntilExp / 100));
    const data = [];

    for (let day = 0; day <= daysUntilExp; day += step) {
      const T_remaining = Math.max((daysUntilExp - day) / 365, 0.0001);
      const theoreticalPremium = blackScholes(priceTarget, strike, T_remaining, RISK_FREE_RATE, effectiveIv, optionType);
      const pnl = isSell
        ? (baselinePremium - theoreticalPremium) * 100
        : (theoreticalPremium - baselinePremium) * 100;

      const date = new Date(today.getTime() + day * 24 * 60 * 60 * 1000);
      const label = day === daysUntilExp
        ? 'EXP'
        : `${date.getMonth() + 1}/${date.getDate()}`;

      data.push({ day, label, pnl: parseFloat(pnl.toFixed(2)) });
    }

    // Always include expiration
    const lastDay = data[data.length - 1]?.day;
    if (lastDay !== daysUntilExp) {
      const theoreticalPremium = blackScholes(priceTarget, strike, 0.0001, RISK_FREE_RATE, effectiveIv, optionType);
      const pnl = isSell
        ? (baselinePremium - theoreticalPremium) * 100
        : (theoreticalPremium - baselinePremium) * 100;
      data.push({ day: daysUntilExp, label: 'EXP', pnl: parseFloat(pnl.toFixed(2)) });
    }

    return data;
  }, [priceTarget, strike, effectiveIv, optionType, isSell, baselinePremium, daysUntilExp, today]);

  // P&L at the selected daysToExit
  const pnlAtExit = useMemo(() => {
    const T = Math.max((daysUntilExp - daysToExit) / 365, 0.0001);
    const theoreticalPremium = blackScholes(priceTarget, strike, T, RISK_FREE_RATE, effectiveIv, optionType);
    return isSell
      ? (baselinePremium - theoreticalPremium) * 100
      : (theoreticalPremium - baselinePremium) * 100;
  }, [priceTarget, daysToExit, daysUntilExp, strike, effectiveIv, optionType, isSell, baselinePremium]);

  // Estimated contract price at target
  const estimatedPremium = useMemo(() => {
    const T = Math.max((daysUntilExp - daysToExit) / 365, 0.0001);
    return blackScholes(priceTarget, strike, T, RISK_FREE_RATE, effectiveIv, optionType);
  }, [priceTarget, daysToExit, daysUntilExp, strike, effectiveIv, optionType]);

  // Dynamic breakeven via binary search — uses the same baseline as P&L so
  // the breakeven line matches the curve (avg cost vs current).
  const breakeven = useMemo(() => {
    const T = Math.max((daysUntilExp - daysToExit) / 365, 0.0001);
    return findBreakevenPrice(strike, baselinePremium, T, effectiveIv, optionType, isSell, RISK_FREE_RATE);
  }, [daysToExit, daysUntilExp, strike, effectiveIv, optionType, isSell, baselinePremium]);

  // Percentage gain (relative to dollars at risk = baseline × 100)
  const pctGain = baselinePremium > 0 ? (pnlAtExit / (baselinePremium * 100)) * 100 : 0;

  // Max loss is your dollars at risk: cost paid (buy) or strike − premium received (cash-secured put).
  const maxLossValue = baselinePremium * 100;
  const maxLossLabel = isSell
    ? (optionType === 'call' ? 'Unlimited' : `$${((strike - baselinePremium) * 100).toFixed(0)}`)
    : `$${maxLossValue.toFixed(0)}`;

  // Chart Y-axis bounds
  const yMin = Math.min(...pnlData.map(d => d.pnl));
  const yMax = Math.max(...pnlData.map(d => d.pnl));
  const yPadding = Math.max(Math.abs(yMax - yMin) * 0.1, 10);
  const chartYMin = yMin - yPadding;
  const chartYMax = yMax + yPadding;

  // Gradient
  const allPositive = pnlData.every(d => d.pnl >= 0);
  const allNegative = pnlData.every(d => d.pnl <= 0);
  const zeroOffset = chartYMax <= chartYMin ? 0.5 : chartYMax / (chartYMax - chartYMin);

  // X-axis ticks
  const tickIndices = useMemo(() => {
    const count = Math.min(pnlData.length, 7);
    const step = Math.max(1, Math.floor((pnlData.length - 1) / (count - 1)));
    const indices = [];
    for (let i = 0; i < pnlData.length; i += step) {
      indices.push(pnlData[i].day);
    }
    const lastDay = pnlData[pnlData.length - 1]?.day;
    if (!indices.includes(lastDay)) indices.push(lastDay);
    return indices;
  }, [pnlData]);

  // Click on chart → update daysToExit
  // Recharts passes the active data point via activePayload on the click event
  const handleChartClick = useCallback((data) => {
    // data can be the chart event object or null
    if (!data) return;

    // Recharts onClick gives us activePayload (array of active data points)
    const payload = data?.activePayload;
    if (payload && payload.length > 0) {
      const clickedDay = payload[0]?.payload?.day;
      if (typeof clickedDay === 'number' && clickedDay >= 1 && clickedDay <= daysUntilExp) {
        setDaysToExit(clickedDay);
      }
      return;
    }

    // Fallback: if activePayload isn't available, try to extract from activeLabel
    if (data.activeLabel != null) {
      const day = Number(data.activeLabel);
      if (!isNaN(day) && day >= 1 && day <= daysUntilExp) {
        setDaysToExit(day);
      }
    }
  }, [daysUntilExp]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && onClose) onClose();
  };

  // Custom tooltip — shows P&L, % gain, and "click to set exit"
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.[0]) return null;
    const { label, pnl, day } = payload[0].payload;
    const isCurrentExit = day === daysToExit;
    const tooltipPctGain = baselinePremium > 0 ? (pnl / (baselinePremium * 100)) * 100 : 0;
    return (
      <div style={{
        backgroundColor: '#1a1a1a',
        border: `1px solid ${isCurrentExit ? '#218aff' : '#333'}`,
        borderRadius: '8px',
        padding: '8px 12px',
        fontSize: '12px',
      }}>
        <div style={{ color: '#b0b0b0' }}>{label} ({day}d)</div>
        <div style={{ color: pnl >= 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>
          P&L: {pnl >= 0 ? '+' : ''}${pnl.toFixed(2)} ({tooltipPctGain >= 0 ? '+' : ''}{tooltipPctGain.toFixed(1)}%)
        </div>
        {!isCurrentExit && (
          <div style={{ color: '#218aff', fontSize: '10px', marginTop: '4px' }}>
            Click to set exit day
          </div>
        )}
      </div>
    );
  };

  // Format exit date
  const exitDate = new Date(today.getTime() + daysToExit * 24 * 60 * 60 * 1000);
  const formatDate = (d) => `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;

  // Format gain label
  const formatGainLabel = (v) => v > 0 ? `+${v}%` : `${v}%`;

  // Slider % fill for underlying gain (-90 to 200 range on the slider, hot buttons go higher)
  const sliderMax = 200;
  const sliderMin = -90;
  const clampedGain = Math.min(underlyingGainPct, sliderMax);
  const sliderFillPct = ((clampedGain - sliderMin) / (sliderMax - sliderMin)) * 100;

  // When embedded (no onClose), render without overlay/modal wrapper
  const isEmbedded = !onClose;

  const content = (
    <div className={isEmbedded ? "flex flex-col h-full overflow-y-auto" : "bg-[#111] rounded-lg w-full max-w-3xl mx-4 flex flex-col overflow-y-auto"} style={isEmbedded ? undefined : { maxHeight: '90vh' }}>
        {/* Header */}
        <div className="px-6 pt-5 pb-3">
          <div className="flex items-center justify-between mb-1">
            <div className="text-sm text-gray-400">
              {ticker} ${strike} {optionType} &middot; {daysUntilExp} DTE
            </div>
            {!isEmbedded && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition p-1 rounded hover:bg-gray-700"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Headline P&L */}
          <div
            className="text-3xl font-bold"
            style={{ color: pnlAtExit >= 0 ? '#10b981' : '#ef4444' }}
          >
            {pnlAtExit >= 0 ? '+' : ''}{formatDollar(pnlAtExit)} ({pnlAtExit >= 0 ? '+' : ''}{pctGain.toFixed(1)}%)
          </div>
          <div className="text-sm text-gray-400 mt-1">
            ${estimatedPremium.toFixed(2)} estimated contract price &middot; ${baselinePremium.toFixed(2)} {baselineMode === BASELINE_MODES.AVG_COST && baselinePremium !== currentPremium ? 'avg cost' : 'entry'}
          </div>
        </div>

        {/* Metrics Row */}
        <div className="grid grid-cols-4 gap-3 px-6 py-3">
          <MetricCard
            label="Delta"
            value={delta !== 'NA' && delta != null ? parseFloat(delta).toFixed(2) : 'N/A'}
          />
          <MetricCard
            label="Theta/day"
            value={theta !== 'NA' && theta != null ? `$${parseFloat(theta).toFixed(2)}` : 'N/A'}
          />
          <MetricCard
            label="Breakeven"
            value={breakeven != null ? `$${breakeven.toFixed(2)}` : 'N/A'}
            dynamic
          />
          <IvCard
            currentIv={effectiveIv}
            originalIv={iv}
            modified={ivModified}
            onUp={() => setCustomIv(Math.min((customIv !== null ? customIv : iv) + 0.01, 2.0))}
            onDown={() => setCustomIv(Math.max((customIv !== null ? customIv : iv) - 0.01, 0.01))}
            onReset={() => setCustomIv(null)}
          />
        </div>

        {/* P&L over Time Chart — clickable */}
        <div className="px-2 py-2" style={{ height: 280, cursor: 'crosshair' }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={pnlData}
              margin={{ top: 10, right: 20, left: 10, bottom: 0 }}
              onClick={handleChartClick}
            >
              <defs>
                <linearGradient id="pnlStroke" x1="0" y1="0" x2="0" y2="1">
                  {allPositive ? (
                    <stop offset="0%" stopColor="#10b981" />
                  ) : allNegative ? (
                    <stop offset="0%" stopColor="#ef4444" />
                  ) : (
                    <>
                      <stop offset="0%" stopColor="#10b981" />
                      <stop offset={`${zeroOffset * 100}%`} stopColor="#10b981" />
                      <stop offset={`${zeroOffset * 100}%`} stopColor="#ef4444" />
                      <stop offset="100%" stopColor="#ef4444" />
                    </>
                  )}
                </linearGradient>
                <linearGradient id="pnlFill" x1="0" y1="0" x2="0" y2="1">
                  {allPositive ? (
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.15} />
                  ) : allNegative ? (
                    <stop offset="0%" stopColor="#ef4444" stopOpacity={0.15} />
                  ) : (
                    <>
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.15} />
                      <stop offset={`${zeroOffset * 100}%`} stopColor="#10b981" stopOpacity={0.02} />
                      <stop offset={`${zeroOffset * 100}%`} stopColor="#ef4444" stopOpacity={0.02} />
                      <stop offset="100%" stopColor="#ef4444" stopOpacity={0.15} />
                    </>
                  )}
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
              <XAxis
                dataKey="day"
                type="number"
                domain={[0, daysUntilExp]}
                stroke="#555"
                tick={{ fill: '#888', fontSize: 11 }}
                ticks={tickIndices}
                tickFormatter={(day) => {
                  if (day === daysUntilExp) return 'EXP';
                  const d = new Date(today.getTime() + day * 24 * 60 * 60 * 1000);
                  return `${d.getMonth() + 1}/${d.getDate()}`;
                }}
              />
              <YAxis
                stroke="#555"
                tick={{ fill: '#888', fontSize: 11 }}
                tickFormatter={(v) => `$${v.toFixed(0)}`}
                domain={[chartYMin, chartYMax]}
              />
              <Tooltip content={<CustomTooltip />} />

              {/* $0 reference line */}
              <ReferenceLine y={0} stroke="#555" strokeDasharray="4 4" />

              {/* Max loss line (for buy positions) */}
              {!isSell && (
                <ReferenceLine
                  y={-maxLossValue}
                  stroke="#ef4444"
                  strokeDasharray="6 3"
                  strokeOpacity={0.6}
                  label={{ value: 'MAX LOSS', position: 'right', fill: '#ef4444', fontSize: 10 }}
                />
              )}

              {/* Days to exit vertical marker */}
              <ReferenceLine
                x={daysToExit}
                stroke="#218aff"
                strokeDasharray="4 4"
                strokeWidth={1.5}
                label={{ value: `${daysToExit}d`, position: 'insideTopRight', fill: '#218aff', fontSize: 10, offset: 5 }}
              />

              <Area
                type="monotone"
                dataKey="pnl"
                stroke="url(#pnlStroke)"
                strokeWidth={2.5}
                fill="url(#pnlFill)"
                dot={false}
                activeDot={{ r: 5, fill: '#218aff', stroke: '#fff', strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Context labels below chart */}
        <div className="flex justify-between px-6 text-xs text-gray-500">
          <span>Now</span>
          <span style={{ color: '#ef4444' }}>Max loss: {maxLossLabel}</span>
          <span>Exp</span>
        </div>

        {/* Sliders */}
        <div className="px-6 pt-4 pb-2 space-y-4">

          {/* Price Target — % based with actual price display */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-300">
                Underlying Gain
              </label>
              <div className="text-right flex items-center gap-1">
                <span className="text-xs text-gray-400">
                  ${underlyingPrice.toFixed(2)}
                </span>
                <span className="text-xs text-gray-500">→</span>
                <span className="text-sm font-bold text-[#218aff]">
                  {formatGainLabel(underlyingGainPct)}
                </span>
                <span className="text-xs text-gray-500">→</span>
                <span className="text-xs text-gray-400">
                  ${priceTarget.toFixed(2)}
                </span>
              </div>
            </div>
            <input
              type="range"
              min={sliderMin}
              max={sliderMax}
              value={clampedGain}
              onChange={(e) => setUnderlyingGainPct(Number(e.target.value))}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer"
              style={{
                background: underlyingGainPct > sliderMax
                  ? `linear-gradient(to right, #218aff 0%, #218aff 100%)`
                  : `linear-gradient(to right, #218aff 0%, #218aff ${sliderFillPct}%, #374151 ${sliderFillPct}%, #374151 100%)`
              }}
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>-90%</span>
              <span>+55%</span>
              <span>+200%</span>
            </div>

            {/* Hot Buttons */}
            <div className="flex flex-wrap gap-1 mt-2">
              {HOT_BUTTONS.map((value) => (
                <button
                  key={value}
                  onClick={() => setUnderlyingGainPct(value)}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    underlyingGainPct === value
                      ? 'bg-[#218aff] text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {formatGainLabel(value)}
                </button>
              ))}
            </div>
          </div>

          {/* Days to Exit Slider */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-300">Days to exit</label>
              <span className="text-sm font-bold text-[#218aff]">
                {daysToExit}d &middot; {formatDate(exitDate)}
              </span>
            </div>
            <input
              type="range"
              min={1}
              max={daysUntilExp}
              value={daysToExit}
              onChange={(e) => setDaysToExit(Number(e.target.value))}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #218aff 0%, #218aff ${
                  (daysToExit / daysUntilExp) * 100
                }%, #374151 ${
                  (daysToExit / daysUntilExp) * 100
                }%, #374151 100%)`
              }}
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>1d</span>
              <span>{Math.floor(daysUntilExp / 2)}d</span>
              <span>{daysUntilExp}d (Exp)</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 pt-2">
          <p className="text-xs text-gray-500">P&L over time at current price target &middot; Click chart to set exit day</p>
        </div>

        {/* Slider thumb styles */}
        <style>{`
          input[type="range"]::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 18px;
            height: 18px;
            background: #218aff;
            cursor: pointer;
            border-radius: 50%;
            border: 2px solid #1f2937;
          }
          input[type="range"]::-moz-range-thumb {
            width: 18px;
            height: 18px;
            background: #218aff;
            cursor: pointer;
            border-radius: 50%;
            border: 2px solid #1f2937;
          }
          input[type="range"]:focus {
            outline: none;
          }
          input[type="range"]:focus::-webkit-slider-thumb {
            box-shadow: 0 0 0 3px rgba(33, 138, 255, 0.2);
          }
        `}</style>
      </div>
  );

  if (isEmbedded) return content;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center"
      style={{ zIndex: 9999 }}
      onClick={handleOverlayClick}
    >
      {content}
    </div>
  );
}

function MetricCard({ label, value, dynamic }) {
  return (
    <div style={{
      background: '#1a1a1a',
      border: '1px solid #333',
      borderRadius: '8px',
      padding: '10px 12px',
      textAlign: 'center',
    }}>
      <div className="text-base font-bold text-white">{value}</div>
      <div className="text-xs text-gray-400 mt-1">
        {label}
        {dynamic && <span className="text-[#218aff] ml-1">&bull;</span>}
      </div>
    </div>
  );
}

/** Interactive IV card with up/down stepper arrows */
function IvCard({ currentIv, originalIv, modified, onUp, onDown, onReset }) {
  const displayValue = currentIv > 0 ? `${(currentIv * 100).toFixed(0)}%` : 'N/A';
  const originalDisplay = `${(originalIv * 100).toFixed(0)}%`;

  return (
    <div style={{
      background: '#1a1a1a',
      border: modified ? '1px solid #218aff' : '1px solid #333',
      borderRadius: '8px',
      padding: '6px 8px',
      textAlign: 'center',
      transition: 'border-color 0.2s ease',
    }}>
      {/* Value with up/down arrows */}
      <div className="flex items-center justify-center gap-1">
        <button
          onClick={onDown}
          className="p-0.5 rounded hover:bg-gray-700 transition-colors text-gray-400 hover:text-white"
          title="Decrease IV by 5%"
        >
          <ChevronDown className="w-4 h-4" />
        </button>
        <span className="text-base font-bold text-white min-w-[40px]">{displayValue}</span>
        <button
          onClick={onUp}
          className="p-0.5 rounded hover:bg-gray-700 transition-colors text-gray-400 hover:text-white"
          title="Increase IV by 5%"
        >
          <ChevronUp className="w-4 h-4" />
        </button>
      </div>

      {/* Label */}
      <div className="text-xs text-gray-400 mt-0.5">
        IV
        {modified && <span className="text-[#218aff] ml-1">&bull;</span>}
      </div>

      {/* Original value + reset (only when modified) */}
      {modified && (
        <div className="flex items-center justify-center gap-1 mt-1">
          <span className="text-[10px] text-gray-500">was {originalDisplay}</span>
          <button
            onClick={onReset}
            className="text-gray-500 hover:text-[#218aff] transition-colors"
            title="Reset to original IV"
          >
            <RotateCcw className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}

function formatDollar(value) {
  const abs = Math.abs(value);
  if (abs >= 1000) {
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }
  return `$${value.toFixed(2)}`;
}
